import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, map, filter, sortBy } from "lodash";

import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import TableRow from "@material-ui/core/TableRow";

import TableCellStyled from "../../Common/TableCellStyled";
import ScrollableTable from "../../Common/Table/ScrollableTable";
import CloseIcon from "../../Common/CloseIcon";
import PictureROI from "../../Common/PictureROI";
import EllipsedTextNew from "../../Common/EllipsedTextNew";
import ThermalPixel from "./ThermalPixel";
import EventsDialog from "../../Events/EventsDialog";
//import TemperatureGraph from "./TemperatureGraph";

import { getLovValue, getAttributeValue } from "../../../utils/profile";
import { formatDate } from "../../../utils/date";
import {
  getMeasureType,
  isOverThreshold,
  getThresholdEvent,
  displayedTemperature,
} from "../../../utils/temperature";

import { ISO_TYPE } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  dialogPaper: {
    minWidth: "calc(100vw - 50px)",
    minHeight: "calc(100vh - 70px)",
  },
  buttonIcon: {
    marginLeft: "auto",
    color: colors.primary,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  closeIcon: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "10px",
  },
  headerCell: {
    backgroundColor: colors.cardTitleBg,
    border: `3px solid ${colors.white}`,
    minWidth: "150px",
  },
  tableContainer: {
    overflow: "auto",
    margin: "0px 24px 24px",
    maxWidth: "calc(100vw - 98px)",
    maxHeight: "calc(100vh - 188px)",
  },
  colorCircle: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
  },
  tableCell: {
    color: colors.black54,
  },
  link: {
    color: colors.black87,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
};

const columns = [
  {
    name: "site_chrono",
    minWidth: 100,
  },
  {
    name: "profiles_photo",
    minWidth: 150,
  },
  {
    name: "site_tempType",
    minWidth: 150,
  },
  {
    name: "site_minTemp",
    minWidth: 100,
  },
  {
    name: "site_maxTemp",
    minWidth: 100,
  },
  {
    name: "site_avgTemp",
    minWidth: 100,
  },
  {
    name: "site_timestamp",
    minWidth: 100,
  },
  {
    name: "profiles_eventId",
    minWidth: 200,
  },
];

const excludeAttr = [153, 164, 165, 166, 167, 168, 88];

const TemperatureMetrics = (props) => {
  const { classes, language, lov, startTime } = props;
  const [open, setOpen] = React.useState(false);
  const [showAllColumns, setShowAllColumns] = React.useState(false);
  const [focusPhotoEvent, setFocusPhotoEvent] = React.useState();

  const closeFunction = () => {
    if (focusPhotoEvent) {
      setFocusPhotoEvent(null);
    } else {
      setOpen(false);
    }
  };

  const renderTableRow = (item) => {
    const type = getMeasureType(item);
    return (
      <>
        <TableCellStyled className={classes.tableCell}>
          {formatDate(
            new Date(getAttributeValue(item, 13)) - new Date(startTime),
            "mm:ss"
          )}
        </TableCellStyled>
        <TableCellStyled>
          <PictureROI
            key={get(item, ["images", 0, "id"])}
            url={get(item, ["images", 0, "url"])}
            roiOverlay={get(item, ["images", 0, "roi_overlay"])}
            thermalPoint={get(item, ["images", 0, "thermal_pixel_coordinate"])}
            initialSquareSize={{ width: 120, height: 120 }}
            id={get(item, ["images", 0, "id"])}
            onClick={() => setFocusPhotoEvent(item)}
            showRoi={true}
            noZoom
          />
        </TableCellStyled>
        <TableCellStyled>
          <Grid container>
            <Grid
              item
              className={classes.colorCircle}
              style={{
                backgroundColor:
                  type === ISO_TYPE ? colors.isoYellow : colors.bodyPink,
              }}
            />
            <Grid item className="text-54" style={{ marginLeft: "10px" }}>
              {I18n.get(type === ISO_TYPE ? "site_isoTemp" : "site_bodyTemp")}
            </Grid>
          </Grid>
        </TableCellStyled>
        <TableCellStyled className={classes.tableCell}>
          {displayedTemperature(item, type === ISO_TYPE ? 167 : 164, lov)}
        </TableCellStyled>
        <TableCellStyled className={classes.tableCell}>
          {displayedTemperature(item, type === ISO_TYPE ? 166 : 153, lov)}
        </TableCellStyled>
        <TableCellStyled>
          <span
            className="flexCenter"
            style={{
              justifyContent: "center",
              width: "55px",
              height: "24px",
              borderRadius: "2px",
              color: isOverThreshold(
                getAttributeValue(item, type === ISO_TYPE ? 168 : 165),
                getThresholdEvent(item)
              )
                ? colors.threatRed
                : colors.cardGreen,
              backgroundColor: isOverThreshold(
                getAttributeValue(item, type === ISO_TYPE ? 168 : 165),
                getThresholdEvent(item)
              )
                ? colors.threatRed10
                : colors.cardGreen10,
            }}
          >
            {displayedTemperature(item, type === ISO_TYPE ? 168 : 165, lov)}
          </span>
        </TableCellStyled>
        <TableCellStyled className={classes.tableCell}>
          {formatDate(getAttributeValue(item, 13), "hh:mm:ss A")}
        </TableCellStyled>
        <TableCellStyled className={classes.tableCell}>
          {get(item, ["id"])}
        </TableCellStyled>
        {showAllColumns &&
          map(
            sortBy(
              filter(
                get(item, ["attributes"]),
                (attr) => !excludeAttr.includes(attr.id)
              ),
              (attr) => get(attr, ["app_display_seq_numb"])
            ),
            (attr) => (
              <TableCellStyled key={attr.id} className={classes.tableCell}>
                <EllipsedTextNew
                  text={
                    get(attr, ["data_type"]) === "lov(picker)"
                      ? getLovValue(88, get(attr, ["value"]), lov, language)
                      : get(attr, ["data_type"]) === "float" &&
                        get(attr, ["value"])
                      ? parseFloat(get(attr, ["value"])).toFixed(1)
                      : get(attr, ["data_type"]) === "datetime"
                      ? formatDate(get(attr, ["value"]))
                      : get(attr, ["value"]) || "-"
                  }
                  maxWidth={600}
                  maxHeight={150}
                />
              </TableCellStyled>
            )
          )}
      </>
    );
  };

  const renderTempTable = () => {
    const allColumns = [
      ...columns,
      ...map(
        sortBy(
          filter(
            get(props, ["data", "events", 0, "attributes"]),
            (attr) => !excludeAttr.includes(attr.id)
          ),
          (attr) => get(attr, ["app_display_seq_numb"])
        ),
        (attr) => ({
          name: get(attr, [`name_${language}`]),
          id: attr.id,
        })
      ),
    ];
    return (
      <ScrollableTable
        columns={showAllColumns ? allColumns : columns}
        hasMore={false}
        stickyHeader
      >
        {map(
          sortBy(get(props, ["data", "events"]), (e) =>
            getAttributeValue(e, 13)
          ),
          (item, index) => (
            <TableRow
              key={item.id}
              style={{
                backgroundColor:
                  index % 2 === 0 ? colors.white : colors.tableDarkBg,
              }}
            >
              {renderTableRow(item)}
            </TableRow>
          )
        )}
      </ScrollableTable>
    );
  };

  return (
    <>
      <span
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={classes.buttonIcon}
      >
        {`${I18n.get("site_view")} ${I18n.get("site_details")}`}
      </span>
      <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
        <Grid
          container
          direction="column"
          alignItems="stretch"
          style={{
            height: "100%",
            overflow: focusPhotoEvent ? "hidden" : "auto",
          }}
        >
          <Grid item className="flexCenter" style={{ height: "60px" }}>
            <Grid container alignItems="center">
              <Grid item xs={2} />
              {focusPhotoEvent ? (
                <Grid item xs className="text-center text-italic">
                  {I18n.get("site_moveMouseOverImage")}
                </Grid>
              ) : (
                <Grid item xs className="text-87 bold-500 text-center">
                  {I18n.get("site_measuredTemp").toUpperCase()}
                </Grid>
              )}
              <Grid item xs={2} className={classes.closeIcon}>
                <CloseIcon closeFunction={closeFunction} />
              </Grid>
            </Grid>
          </Grid>
          {!focusPhotoEvent && (
            <Grid item>
              <Grid container>
                <Grid
                  item
                  xs={6}
                  className="flexCenter"
                  style={{ paddingLeft: "24px" }}
                >
                  <EventsDialog
                    eventGroupId={get(props, [
                      "data",
                      "events",
                      0,
                      "group",
                      "id",
                    ])}
                    text={`${I18n.get("site_eventGroup")} #${get(props, [
                      "data",
                      "events",
                      0,
                      "group",
                      "id",
                    ])}`}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  className="flexCenter"
                  style={{
                    justifyContent: "flex-end",
                    height: "40px",
                    paddingRight: "24px",
                  }}
                  onClick={() => setShowAllColumns(!showAllColumns)}
                >
                  <span className={classes.link}>
                    {I18n.get(
                      showAllColumns
                        ? "site_hideAdditionalColumns"
                        : "site_viewAdditionalColumns"
                    )}
                  </span>
                  <img
                    src={
                      showAllColumns
                        ? images.arrowOpenBlack
                        : images.arrowClosedBlack
                    }
                    alt="arrow"
                    style={{
                      marginLeft: "10px",
                      transform: !showAllColumns ? "rotate(180deg)" : "",
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {focusPhotoEvent ? (
            <ThermalPixel
              event={focusPhotoEvent}
              lov={lov}
              closeFunction={() => setFocusPhotoEvent()}
            />
          ) : (
            <Grid item className={classes.tableContainer}>
              {renderTempTable()}
            </Grid>
          )}
        </Grid>
      </Dialog>
    </>
  );
};

export default withStyles(styles)(TemperatureMetrics);

/* <Grid item>
  <div style={{ margin: "auto", maxWidth: "600px" }}>
    <TemperatureGraph
      data={props.data}
      startTime={props.startTime}
      lov={lov}
    />
  </div>
</Grid> */
